/* eslint-disable @typescript-eslint/no-explicit-any */
import { useDedupedEffect } from "@/hooks/use-deduped-effect";
import { useStateWithGetter } from "@/hooks/use-state-with-getter";
import {
  getValueOrReturnValue,
  MaybeGetter,
  MaybeGetterValue,
} from "@/types/maybe-getter";
import { MaybePromise } from "@/types/maybe-promise";
import {
  AbsoluteToPath,
  AnyRouteMatch,
  redirect,
  RegisteredRouter,
  useMatches,
  useNavigate,
} from "@tanstack/react-router";
import { atom, useAtom } from "jotai";
import {
  SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { store } from "./jotai";

type InferContextObj<T> = unknown extends T ? { context?: T } : { context: T };

export type StepperSubmitHandler = {
  submit: () => MaybePromise<void>;
  text?: string;
  isDirty?: boolean;
  isDisabled?: boolean;
  isPending?: boolean;
};

type StepperStoreState = {
  submitHandler?: StepperSubmitHandler;
  lastVisitedStepMap: ReadonlyMap<string | null, string>;
};

const createStepperStore = (symbol: symbol) => {
  const createState = (): StepperStoreState => ({
    lastVisitedStepMap: new Map(),
  });

  const stateAtom = atom(createState());

  return {
    stepperSymbol: symbol,
    consumers: new Set<symbol>(),
    getState: () => store.get(stateAtom),
    setState: (action: SetStateAction<StepperStoreState>) =>
      store.set(stateAtom, action),
    useStateAtom: () => useAtom(stateAtom),
  };
};

type StepperStore = ReturnType<typeof createStepperStore>;

const stepperStores = new Map<symbol, StepperStore>();

const useStepperStore = (stepperSymbol: symbol): StepperStore => {
  const consumerSymbolRef = useRef<symbol | null>(null);
  consumerSymbolRef.current ??= Symbol();
  const consumerSymbol = consumerSymbolRef.current;

  const ref = useRef<StepperStore | null>(null);

  let stepperStore = stepperStores.get(stepperSymbol);
  if (!stepperStore) {
    stepperStore = createStepperStore(stepperSymbol);
    stepperStores.set(stepperSymbol, stepperStore);
  }

  stepperStore.consumers.add(consumerSymbol);

  ref.current = stepperStore;

  // Clean up when unmounted.
  useDedupedEffect(() => {
    return () => {
      ref.current = null;

      stepperStore.consumers.delete(consumerSymbol);

      if (stepperStore.consumers.size === 0) {
        if (stepperStores.get(stepperSymbol) !== stepperStore) return;
        stepperStores.delete(stepperSymbol);
      }
    };
  }, stepperSymbol);

  if (!ref.current) throw Error("Stepper store not defined");

  return ref.current;
};

const defaultStepperSymbol = Symbol("Default stepper symbol");

export function createStepper<
  TContextObj,
  TStepId extends string,
  TStep extends CreateStepOptions<
    InferContextObj<TContextObj>["context"],
    TStepId
  >,
>(
  options: InferContextObj<TContextObj> & {
    /**
     * An optional symbol to identify the stepper.
     * Required if you want to use the `useStepperSubmit` hook.
     */
    symbol?: symbol;
    steps: TStep[];
    treatLockedAsNotVisible?: boolean;

    /**
     * Requires each step to be marked as completed before accessing the next step.
     * Completed means that the user has visited the step and pressed the next/submit button.
     * Regardless of this option, steps are still required to be valid to access the next step.
     * This only adds an additional requirement of being completed.
     */
    requireCompleteToAccessNextStep?: boolean;
    getCompletedStepIds?: (
      context: InferContextObj<TContextObj>["context"],
    ) => Set<string>;
    setCompletedStepIds?: (
      newCompletedStepIds: Set<string>,
      setContext: (
        action: (
          prev: InferContextObj<TContextObj>["context"],
        ) => InferContextObj<TContextObj>["context"],
      ) => void,
    ) => void;

    /**
     * Optional: Define custom getter and setter for the last visited step map.
     * Falls back to internal stepperStore logic if not provided.
     * The map works like this:
     * Key: a step id or `null` (representing the root of the stepper).
     * Value: the step id of the last visited step within the key step's subtree.
     */
    getLastVisitedStepMap?: (
      context: InferContextObj<TContextObj>["context"],
    ) => ReadonlyMap<string | null, string>;
    setLastVisitedStepMap?: (
      newLastVisitedStepMap: ReadonlyMap<string | null, string>,
    ) => void;
  },
) {
  const context = options.context as (typeof options)["context"];
  type TContext = typeof context;

  type TLooseStep = MappedStep<CreateStepOptions<any, string>>;
  type TFlatStep = FlatStep<TStep>;

  const symbol = options.symbol ?? defaultStepperSymbol;

  const createStepperState = ({
    context,
    stepperStoreState,
  }: {
    context: TContext;
    stepperStoreState?: StepperStoreState;
  }): StepperState<TContext, TStepId, TStep> => {
    stepperStoreState ??= stepperStores.get(symbol)?.getState();

    function mapStep<TStep extends CreateStepOptions<any, string>>(
      step: TStep,
      parentStatuses: {
        isVisible: boolean;
        isLocked: boolean;
      } = {
        isVisible: true,
        isLocked: false,
      },
    ): MappedStep<TStep> {
      let isVisible =
        parentStatuses.isVisible && (step.getIsVisible?.(context) ?? true);
      let isValid = step.getIsValid?.(context) ?? true;
      let isLocked =
        parentStatuses.isLocked || (step.getIsLocked?.(context) ?? false);
      let isCompleted =
        options.getCompletedStepIds?.(context).has(step.id) ?? false;

      if (options.treatLockedAsNotVisible && isLocked) {
        isVisible = false;
      }

      const substeps = (step.steps || []) as NonNullable<TStep["steps"]>;

      const mapSubstepsResult = mapSteps(substeps, {
        isVisible,
        isLocked,
      });

      if (mapSubstepsResult.steps.length) {
        isVisible = isVisible && mapSubstepsResult.visibleSteps.length > 0;

        isValid =
          isValid && mapSubstepsResult.visibleSteps.every((s) => s.isValid);
        isLocked =
          isLocked || mapSubstepsResult.visibleSteps.every((s) => s.isLocked);
        isCompleted =
          isCompleted ||
          mapSubstepsResult.visibleSteps.every((s) => s.isCompleted);
      }

      return {
        id: step.id,
        name: getValueOrReturnValue(step.name, context),
        to: step.to,
        description: step.description ?? "",
        getIsVisible: step.getIsVisible,
        isVisible,
        isValid,
        isLocked,
        isCompleted,
        canBeAccessed: isVisible,
        canBeAccessedWhenPreviousIsCompleted: isVisible,
        ...mapSubstepsResult,
      } as MappedStep<TStep>;
    }

    function mapSteps<TStep extends CreateStepOptions<any, string>>(
      steps: TStep[],
      parentStatuses?: {
        isVisible: boolean;
        isLocked: boolean;
      },
    ): MappedSteps<TStep> {
      const mappedSteps = steps.map((step) => mapStep(step, parentStatuses));

      return {
        steps: mappedSteps,
        visibleSteps: mappedSteps.filter((step) => step.isVisible),
        stepsById: Object.fromEntries(
          mappedSteps.map((step) => [step.id, step]),
        ) as unknown as {
          [Id in TStep["id"]]: MappedStep<Extract<TStep, { id: Id }>>;
        },
        stepsByTo: new Map(mappedSteps.map((step) => [step.to, step])),
        indicesById: new Map(
          mappedSteps.map((step, index) => [step.id, index]),
        ),
      };
    }

    const mappedSteps = mapSteps(options.steps);

    const flatMapSteps = (steps: TLooseStep[]): any[] =>
      steps.flatMap((step) => [step, ...flatMapSteps(step.steps)]);

    const flatSteps: TFlatStep[] = flatMapSteps(mappedSteps.steps);
    const flatVisibleSteps = flatSteps.filter((step) => step.isVisible);

    const flatStepsById = Object.fromEntries(
      flatSteps.map((step) => [step.id, step]),
    ) as { [Id in TFlatStep["id"]]: Extract<TFlatStep, { id: Id }> };

    const flatStepsByTo = new Map(flatSteps.map((step) => [step.to, step]));

    // Look for a visible, but invalid step, then mark all following steps as canBeAccessed = false and canBeAccessedWhenPreviousIsCompleted = false.
    {
      let foundInvalidStep = false;

      const processSteps = (steps: TLooseStep[]) => {
        for (const step of steps) {
          if (foundInvalidStep) {
            step.canBeAccessed = false;
            step.canBeAccessedWhenPreviousIsCompleted = false;
          }

          processSteps(step.visibleSteps);

          if (!step.isValid) {
            foundInvalidStep = true;
            continue;
          }
        }
      };

      processSteps(mappedSteps.visibleSteps);
    }

    // Mark following steps as canBeAccessedWhenPreviousIsCompleted = true if the option is enabled and there's a leaf step that is not completed.
    if (options.requireCompleteToAccessNextStep) {
      for (const [i, step] of flatVisibleSteps.entries()) {
        if (!step.canBeAccessed) {
          // No need to continue, since the following steps are marked as inaccessible by the previous logic.
          break;
        }

        if (!step.steps.length) {
          if (!step.isCompleted) {
            const followingSteps = flatVisibleSteps.slice(i + 1);

            let leafStepFound = false;

            // Mark all following steps as canBeAccessedWhenPreviousIsCompleted = true until the next leaf step is found. After a leaf step is found, the following steps should be marked as canBeAccessedWhenPreviousIsCompleted = false, since they should only be accessible when the previous step is completed, not when the previous step is completed and it's the last visited step.
            for (const followingStep of followingSteps) {
              if (followingStep.canBeAccessed) {
                followingStep.canBeAccessed = false;
                followingStep.canBeAccessedWhenPreviousIsCompleted =
                  !leafStepFound;
              } else {
                // No need to continue, since the following steps are marked as inaccessible by the previous logic.
                break;
              }

              if (!followingStep.steps.length) {
                leafStepFound = true;
              }
            }

            break;
          }
        }
      }
    }

    return {
      ...mappedSteps,
      flatSteps,
      flatVisibleSteps,
      flatStepsById,
      flatStepsByTo,
    };
  };

  const stepperState = createStepperState({ context });

  const getLastVisitedStepMap = (
    stepperStore: StepperStore,
    context: TContext,
  ): ReadonlyMap<string | null, string> => {
    if (options.getLastVisitedStepMap) {
      return options.getLastVisitedStepMap(context);
    }
    return stepperStore.getState().lastVisitedStepMap;
  };

  const setLastVisitedStepMap = (
    stepperStore: StepperStore,
    context: TContext,
    action: (
      prev: ReadonlyMap<string | null, string>,
    ) => ReadonlyMap<string | null, string>,
  ) => {
    const lastVisitedStepMap = getLastVisitedStepMap(stepperStore, context);
    const newLastVisitedStepMap = action(lastVisitedStepMap);

    if (options.setLastVisitedStepMap) {
      options.setLastVisitedStepMap(newLastVisitedStepMap);
    }

    stepperStore.setState((prev) => ({
      ...prev,
      lastVisitedStepMap: newLastVisitedStepMap,
    }));
  };

  const onBeforeLoadStepRoute = (
    stepperIndexPath: StepTo,
    matches: AnyRouteMatch[],
  ): void => {
    let stepperStore = stepperStores.get(symbol);
    if (!stepperStore) {
      stepperStore = createStepperStore(symbol);
      stepperStores.set(symbol, stepperStore);
    }

    const steps: TLooseStep[] = [];
    let currentStep: TLooseStep | null = null;

    for (const match of [...matches].reverse()) {
      if (match.fullPath === "") continue;

      const fullPath = removeTrailingSlash(match.fullPath);

      const step = stepperState.flatStepsByTo.get(fullPath) ?? null;
      if (!step) continue;

      steps.push(step);
      currentStep ??= step;
    }

    if (!currentStep && matches.at(-1)?.fullPath !== stepperIndexPath) return;

    if (!currentStep || currentStep.canBeAccessed) {
      const lastVisitedStepMap = getLastVisitedStepMap(stepperStore, context);
      const lastVisitedStepId = lastVisitedStepMap.get(currentStep?.id ?? null);
      const lastVisitedStep =
        (lastVisitedStepId &&
          lastVisitedStepId in stepperState.flatStepsById &&
          stepperState.flatStepsById[
            lastVisitedStepId as keyof typeof stepperState.flatStepsById
          ]) ||
        null;
      if (lastVisitedStep && lastVisitedStep.id !== currentStep?.id) {
        throw redirect({ to: lastVisitedStep.to });
      }
    }

    let newStep = null as TLooseStep | null;

    const processSteps = (
      steps: TLooseStep[],
      mode: "first" | "lastUntilCurrent",
    ) => {
      for (const step of steps) {
        if (!step.canBeAccessed) return "stop";

        if (step.steps.length) {
          const result = processSteps(step.visibleSteps, mode);
          if (result === "stop") return "stop";
          if (mode === "lastUntilCurrent" && step.id === currentStep?.id)
            return "stop";
          continue;
        }

        if (!step.isLocked) {
          newStep = step;
          if (mode === "first") return "stop";
        }
        if (mode === "lastUntilCurrent" && step.id === currentStep?.id)
          return "stop";
      }
    };

    if (currentStep && !currentStep.steps.length) {
      processSteps(stepperState.visibleSteps, "lastUntilCurrent");
    } else {
      processSteps(
        currentStep?.visibleSteps ?? stepperState.visibleSteps,
        "first",
      );
    }

    if (newStep && newStep !== currentStep) {
      throw redirect({
        to: newStep.to,
      });
    }

    // Mark last visited steps
    if (currentStep) {
      setLastVisitedStepMap(
        stepperStore,
        context,
        (prev) =>
          new Map([
            ...prev,
            // Mark as last visited step for blank step (root of the stepper)
            [null, currentStep.id],
            // Mark as last visited step for all all parent steps.
            ...steps.map((step) => [step.id, currentStep.id] as const),
          ]),
      );
    }
  };

  const useStepper = (
    symbol: symbol,
    _options: typeof options,
    props: {
      context: TContext;
      setContext?: (action: (prev: TContext) => TContext) => void;
    },
  ): UseStepperResult<TContext, TStepId, TStep> => {
    const options = _options;

    const matches = useMatches();

    const createActiveStepsObject = useCallback(
      (
        stepperState: StepperState<TContext, TStepId, TStep>,
        matches: ReturnType<typeof useMatches>,
      ) => {
        const activeStepTos = new Set<StepTo>();

        for (const match of [...matches].reverse()) {
          if (match.fullPath === "") continue;

          const stepTo = removeTrailingSlash(match.fullPath);
          activeStepTos.add(stepTo);
        }

        const activeFlatSteps = new Set<TFlatStep>();
        let lastActiveFlatStep: TFlatStep | null = null;

        for (const step of stepperState.flatVisibleSteps) {
          if (!step.canBeAccessed) break;

          if (activeStepTos.has(step.to)) {
            activeFlatSteps.add(step);
            lastActiveFlatStep = step;
          }
        }

        const lastActiveFlatStepIndex = lastActiveFlatStep
          ? stepperState.flatVisibleSteps.indexOf(lastActiveFlatStep)
          : -1;

        let prevStep: TFlatStep | null = null;
        let nextStep: TFlatStep | null = null;

        for (let index = lastActiveFlatStepIndex - 1; index >= 0; index--) {
          const step = stepperState.flatVisibleSteps[index];
          if (!step.steps.length) {
            prevStep = step;
            break;
          }
        }

        for (
          let index = lastActiveFlatStepIndex + 1;
          index < stepperState.flatVisibleSteps.length;
          index++
        ) {
          const step = stepperState.flatVisibleSteps[index];
          if (!step.steps.length) {
            nextStep = step;
            break;
          }
        }

        return {
          activeFlatSteps,
          lastActiveFlatStep,
          lastActiveFlatStepIndex,
          prevStep,
          nextStep,
        };
      },
      [],
    );

    const createState = useCallback(
      ({
        context,
        stepperStoreState,
        matches,
      }: {
        context: TContext;
        stepperStoreState?: StepperStoreState;
        matches: ReturnType<typeof useMatches>;
      }) => {
        const stepperState = createStepperState({ context, stepperStoreState });

        return {
          ...stepperState,
          context,
          ...createActiveStepsObject(stepperState, matches),
        };
      },
      [createActiveStepsObject],
    );

    const [state, getState, setState] = useStateWithGetter(() =>
      createState({ context: props.context, matches }),
    );

    useEffect(() => {
      if (props.context === getState().context) return;
      setState(createState({ context: props.context, matches }));
    }, [createState, getState, matches, props.context, setState]);

    useEffect(() => {
      setState((prev) => ({
        ...prev,
        ...createActiveStepsObject(prev, matches),
      }));
    }, [createActiveStepsObject, matches, setState]);

    const setContext = useCallback(
      (action: (prev: TContext) => TContext) => {
        if (!props.setContext) return;
        props.setContext((prev) => {
          const newContext = action(prev);
          setState(createState({ context: newContext, matches }));
          return newContext;
        });
      },
      [createState, matches, props, setState],
    );

    const setCompletedStepIds = useCallback(
      (action: (prev: Set<string>) => Set<string>) => {
        if (!options.getCompletedStepIds || !options.setCompletedStepIds)
          return;
        const prevCompletedStepIds = options.getCompletedStepIds(
          getState().context,
        );
        const newCompletedStepIds = action(prevCompletedStepIds);
        options.setCompletedStepIds(newCompletedStepIds, setContext);
      },
      [getState, setContext, options],
    );

    const navigate = useNavigate();

    const stepperStore = useStepperStore(symbol);
    const [stepperStoreState] = stepperStore.useStateAtom();

    const providedSubmitHandler = stepperStoreState.submitHandler;

    const { nextIsSubmit, submitText, isSubmitting } = useMemo(
      () => ({
        nextIsSubmit: !!providedSubmitHandler,
        submitText: providedSubmitHandler?.text,
        isSubmitting: providedSubmitHandler?.isPending ?? false,
      }),
      [providedSubmitHandler],
    );

    const canGoPrev = useMemo(() => {
      if (providedSubmitHandler?.isPending) return false;
      const { prevStep } = state;
      return prevStep?.canBeAccessed ?? false;
    }, [providedSubmitHandler?.isPending, state]);

    const canGoNext = useMemo(() => {
      if (providedSubmitHandler?.isPending) return false;
      const { nextStep } = state;
      if (nextStep)
        return (
          nextStep.canBeAccessed ||
          nextStep.canBeAccessedWhenPreviousIsCompleted
        );
      return false;
    }, [providedSubmitHandler?.isPending, state]);

    const canSubmitOrGoNext = useMemo(() => {
      return providedSubmitHandler
        ? providedSubmitHandler.isDisabled !== true &&
            providedSubmitHandler.isPending !== true
        : canGoNext;
    }, [canGoNext, providedSubmitHandler]);

    const goPrev = useCallback(async () => {
      const { prevStep } = getState();
      if (prevStep)
        await navigate({
          to: prevStep.to,
        });
    }, [getState, navigate]);

    const goNext = useCallback(async () => {
      const state = getState();

      const { nextStep, lastActiveFlatStep } = state;
      if (!nextStep) return;

      if (lastActiveFlatStep) {
        setCompletedStepIds(
          (prev) => new Set([...prev, lastActiveFlatStep.id]),
        );
      }

      await navigate({
        to: nextStep.to,
      });
    }, [getState, navigate, setCompletedStepIds]);

    const submitOrGoNext = useCallback(async () => {
      const providedSubmitHandler = stepperStore.getState().submitHandler;

      if (providedSubmitHandler) {
        await providedSubmitHandler.submit();
        return;
      }

      await goNext();
    }, [goNext, stepperStore]);

    return useMemo(
      () => ({
        ...state,
        setContext,
        nextIsSubmit,
        submitText,
        isSubmitting,
        canGoPrev,
        canGoNext,
        canSubmitOrGoNext,
        goPrev,
        goNext,
        submitOrGoNext,
      }),
      [
        canGoNext,
        canGoPrev,
        canSubmitOrGoNext,
        goNext,
        goPrev,
        nextIsSubmit,
        submitText,
        isSubmitting,
        setContext,
        state,
        submitOrGoNext,
      ],
    );
  };

  const useStepperSubmit = (
    symbol: symbol,
    {
      submit: submitFunction,
      text,
      isDirty,
      isDisabled,
      isPending,
    }: StepperSubmitHandler,
  ) => {
    const submitFunctionRef = useRef(submitFunction);

    const submit = useCallback(() => submitFunctionRef.current(), []);

    const submitHandler = useMemo(
      () => ({ submit, text, isDirty, isDisabled, isPending }),
      [submit, text, isDirty, isDisabled, isPending],
    );

    const stepperStore = useStepperStore(symbol);

    useEffect(() => {
      stepperStore.setState((prev) => {
        if (prev.submitHandler)
          throw Error("A submit handler for this stepper already exists");
        return { ...prev, submitHandler };
      });

      return () => {
        stepperStore.setState((prev) => {
          if (prev.submitHandler !== submitHandler)
            throw Error("Stepper submit handler mismatch on cleanup");
          return { ...prev, submitHandler: undefined };
        });
      };
    }, [stepperStore, submitHandler]);
  };

  type ParametersExceptFirst<F> = F extends (
    first: infer _First,
    ...args: infer Rest
  ) => infer _Return
    ? Rest
    : never;

  return {
    stepperState,
    onBeforeLoadStepRoute,
    useStepper: (props: Parameters<typeof useStepper>[2]) =>
      useStepper(symbol, options, props),
    useStepperSubmit: (
      ...args: ParametersExceptFirst<typeof useStepperSubmit>
    ) => useStepperSubmit(symbol, ...args),
  };
}

export type StepperState<
  TContext = any,
  TStepId extends string = string,
  TStep extends CreateStepOptions<TContext, TStepId> = CreateStepOptions<
    TContext,
    TStepId
  >,
> = {
  flatSteps: FlatStep<TStep>[];
  flatVisibleSteps: FlatStep<TStep>[];
  flatStepsById: {
    [Id in FlatStep<TStep>["id"]]: Extract<
      FlatStep<TStep>,
      {
        id: Id;
      }
    >;
  };
  flatStepsByTo: Map<StepTo, FlatStep<TStep>>;
} & MappedSteps<TStep>;

type Exact<T, Shape> = T extends Shape
  ? keyof T extends keyof Shape
    ? T
    : never
  : never;

export type UseStepperResult<
  TContext = any,
  TStepId extends string = string,
  TStep extends CreateStepOptions<TContext, TStepId> = CreateStepOptions<
    TContext,
    TStepId
  >,
> = StepperState<TContext, TStepId, TStep> & {
  context: TContext;
  setContext: <T extends TContext>(
    action: (prev: TContext) => Exact<T, TContext>,
  ) => void;
  activeFlatSteps: Set<FlatStep<TStep>>;
  lastActiveFlatStep: FlatStep<TStep> | null;
  lastActiveFlatStepIndex: number;
  nextIsSubmit: boolean;
  submitText?: string;
  hideSubmitButton?: boolean;
  isSubmitting: boolean;
  canGoPrev: boolean;
  canGoNext: boolean;
  canSubmitOrGoNext: boolean;
  prevStep: FlatStep<TStep> | null;
  nextStep: FlatStep<TStep> | null;
  goPrev: () => void;
  goNext: () => void;
  submitOrGoNext: () => void;
};

export type StepTo = AbsoluteToPath<RegisteredRouter, "/">;

export type CreateStepOptions<Context, Id extends string> = {
  id: Id;
  name: MaybeGetter<string, Context>;
  description?: string;
  to: StepTo;
  steps?: CreateStepOptions<Context, Id>[];
  getIsVisible?: (context: Context) => boolean;
  getIsValid?: (context: Context) => boolean;
  getIsLocked?: (context: Context) => boolean;
};

export type MappedStep<T extends CreateStepOptions<any, string>> = T extends T
  ? {
      id: T["id"];
      name: MaybeGetterValue<T["name"]>;
      description: string;
      to: T["to"];
      getIsVisible: T["getIsVisible"];
      isVisible: boolean;
      isValid: boolean;
      isLocked: boolean;
      isCompleted: boolean;
      canBeAccessed: boolean;
      canBeAccessedWhenPreviousIsCompleted: boolean;
    } & MappedSteps<NonNullable<T["steps"]>[number]>
  : never;

export type MappedSteps<T extends CreateStepOptions<any, string>> = {
  steps: MappedStep<T>[];
  visibleSteps: MappedStep<T>[];
  stepsById: { [Id in T["id"]]: MappedStep<Extract<T, { id: Id }>> };
  stepsByTo: Map<MappedStep<T>["to"], MappedStep<T>>;
  indicesById: Map<MappedStep<T>["id"], number>;
};

export type MappedStepsArray<T> = T extends CreateStepOptions<any, string>[]
  ? MappedStep<InferStep<T[number]>>[]
  : [];

export type OptionalArrayValue<T> = T extends (infer U)[] ? U : never;

export type InferStepSubstep<T extends CreateStepOptions<any, string>> =
  T["steps"] extends CreateStepOptions<any, string>[]
    ? T["steps"][number]
    : never;

export type InferStep<T> = T extends CreateStepOptions<any, string> ? T : never;

export type FlatStep<T> =
  T extends CreateStepOptions<infer _TContext, infer _TId>
    ? T["steps"] extends (infer U)[]
      ? MappedStep<T> | FlatStep<U>
      : MappedStep<T>
    : never;

function removeTrailingSlash<T extends string>(path: T) {
  if (!path.endsWith("/") || path === "/")
    return path as Exclude<T, `${string}/`> | Extract<T, "/">;
  return path.slice(0, -1) as Exclude<T, `${string}/`>;
}

export function useStepperSubmitHandlerState() {
  const submitFunctionRef = useRef<(() => void) | null>(null);
  const [submitText, setSubmitText] = useState<string>();
  const [submitIsDirty, setSubmitIsDirty] = useState<boolean>();
  const [submitIsDisabled, setSubmitIsDisabled] = useState<boolean>();
  const [submitIsPending, setSubmitIsPending] = useState<boolean>();

  const submitHandler = useMemo<StepperSubmitHandler>(
    () => ({
      submit: () => submitFunctionRef.current?.(),
      text: submitText,
      isDirty: submitIsDirty,
      isDisabled: submitIsDisabled,
      isPending: submitIsPending,
    }),
    [submitText, submitIsDirty, submitIsDisabled, submitIsPending],
  );

  const setSubmitHandler = useCallback(
    (submitHandler: StepperSubmitHandler) => {
      submitFunctionRef.current = submitHandler.submit;
      setSubmitText(submitHandler.text);
      setSubmitIsDirty(submitHandler.isDirty);
      setSubmitIsDisabled(submitHandler.isDisabled);
      setSubmitIsPending(submitHandler.isPending);
    },
    [],
  );

  return [submitHandler, setSubmitHandler] as const;
}
